import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Icon, Loader, Text, tokens } from '@hotjar/hotsauce';
import type { IconName } from '@hotjar/hotsauce';

import { ListItem } from './ListItem';

import type { SidebarItemAction, SidebarItem, State } from './SideBar';

export interface ListProps<T> {
  isReadOnly?: boolean;
  className?: string;
  title?: string;
  icon?: IconName;
  errorStateText?: string | ReactNode;
  emptyStateText?: string | ReactNode;
  items: T[];
  state: State;
  actions?: SidebarItemAction<T>[];
  onClick: (item: T) => void;
  onItemSave?: (item: T) => void;
  onItemBlur?: () => void;
}

const ListTitle = styled(Text)`
  display: block;
  margin: ${tokens.spacer.x2} 0;
  margin-left: ${tokens.spacer.x1};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ListTitleIcon = styled(Icon)`
  margin: 0 ${tokens.spacer.x1};
`;

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledLoader = styled(Loader)`
  width: ${tokens.spacer.x4};
  margin-left: -${tokens.spacer.x1};
`;

const StyledText = styled(Text)`
  display: block;
  margin-bottom: ${tokens.spacer.x2pt5};
  margin-left: ${tokens.spacer.x1};
`;

export const List = <T extends SidebarItem>({
  errorStateText,
  emptyStateText,
  title,
  icon,
  items,
  className,
  state,
  actions,
  onClick,
  onItemBlur,
  onItemSave,
  isReadOnly,
}: ListProps<T>): JSX.Element => {
  return (
    <div className={className}>
      {title && (
        <ListTitle textType="subtitle3">
          {icon && <ListTitleIcon icon={icon} />}
          {title}
        </ListTitle>
      )}
      {state === 'ready' && (
        <>
          {items?.length === 0 ? (
            <StyledText textType="caption" textColor="text_light">
              {emptyStateText}
            </StyledText>
          ) : (
            <StyledList>
              {items.map((item) => (
                <ListItem
                  key={item.id}
                  item={item}
                  actions={actions}
                  isReadOnly={isReadOnly}
                  onItemSave={onItemSave}
                  onItemBlur={onItemBlur}
                  onClick={() => !item.isEditing && onClick(item)}
                />
              ))}
            </StyledList>
          )}
        </>
      )}
      {state === 'loading' && (
        <StyledText textType="caption" textColor="text_light">
          <StyledLoader size="m" />
        </StyledText>
      )}
      {state === 'failed' && (
        <StyledText textType="caption" textColor="text_light">
          {errorStateText}
        </StyledText>
      )}
    </div>
  );
};
